import { PaymentMethods } from '@/types/enum';
import * as yup from 'yup';
import { ICustomer, OpraStatus } from '@/types';
import { baseEntityFields, insuranceLiabilitySchema, addressSchema,optionaladdressSchema, agentInsurerFields, accountingContactFields, entityDetailsFields, truckSchema } from './CommonSchema';

// Base fields common to both customer types (imported from CommonSchema)
const baseCustomerFields = baseEntityFields;

// Freight/Operations customer specific fields
const freightCustomerFields = {
  nickName: yup.string().optional(),
  usdot: yup.string().optional(),
  mcNumber: yup.string().optional().label("Mc Number"),
  alternatphone: yup.string()
    .label('Alternate Phone No')
    .optional()
    // .test('is-valid-phone', 'Invalid Alternate Phone No number', (value) => {
    //   if (!value) return true;
    //   return validPhoneNumber(value);
    // })
    .when('phone', {
      is: (value: string) => !value,
      then: (schema) => schema.required('Alternate Phone No is required'),
    }),
  extentionNo: yup.string().optional(),
  // address: yup.string().required('Address is required'),
  // city: yup.string().optional(),
  // state: yup.string().optional(),
  // zipCode: yup.string().optional(),
  vatNumber: yup.string().optional(),
  utrNumber: yup.string().optional(),
  contactPersons: yup.array().default([]).of(yup.object().shape({})),
  ...truckSchema,
  ...agentInsurerFields,
  ...insuranceLiabilitySchema,
  ...entityDetailsFields,
  ...optionaladdressSchema,
};

// Freight/Operations customer schema (without accounting fields)
const freightCustomerSchema = yup.object().shape({
  ...baseCustomerFields,
  ...freightCustomerFields,
});

const repairTruckSchema = {
  truckDetails: yup.object().shape({
    vinNumber: yup
      .string()
      .label('VIN No')
      .transform((value) => (value ? String(value).toUpperCase().trim() : value))
      .matches(/^[A-HJ-NPR-Z0-9]{17}$/i, { message: 'VIN No must be exactly 17 characters', excludeEmptyString: true })
      .required('VIN No is required'),
    plateNumber: yup.string().optional(),
    unitNumber: yup.string().optional(),
    color: yup.string().optional(),
    make: yup.string().optional(),
    model: yup.string().optional(),
    year: yup.string().optional(),
  }).required('Truck details are required for repair company'),
};

// Accounting customer schema (without freight fields)
const accountingCustomerSchema = (isRepairCompany: boolean) => {
  return yup.object().shape({
    ...baseCustomerFields,
    ...accountingContactFields,
    ...(!isRepairCompany ? addressSchema : {}),
    ...(isRepairCompany ? repairTruckSchema : {}),
    ...(isRepairCompany ? { email: yup.string().transform((value) => (value === '' ? undefined : value)).email('Invalid email').optional() } : {}),
  })
}

// Default data for freight/operations customer
const defaultFreightCustomerData: ICustomer = {
  company: '',
  nickName: '',
  email: '',
  autoScore: 100,
  stars: 5,
  phone: '',
  alternatphone: '',
  extentionNo: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  mcNumber: '',
  usdot: '',
  paymentMethod: PaymentMethods.NA,
  paymentTerms: '',
  vatNumber: '',
  utrNumber: '',
  _id: '',
  documents: [],
  deleteFiles: [],
  entityDetails: {
    entity_type: '',
    dba_name: '',
    legal_name: '',
    operating_status: '' as OpraStatus,
    physical_address: '',
    mailing_address: '',
    carrier_operation: [],
    out_of_service_date: '',
  },
  withoutUsdot: false,
  insurerCompany: '',
  agentName: '',
  agentAddress: '',
  agentEmail: '',
  agentPhoneNumber: '',
  commercialGeneralLiability: {
    issueDate: undefined,
    expiryDate: undefined,
    amount: undefined
  },
  automobileLiability: {
    issueDate: undefined,
    expiryDate: undefined,
    amount: undefined
  },
  cargoLiability: {
    issueDate: undefined,
    expiryDate: undefined,
    amount: undefined
  },
  insuranceDocuments: [],
  deleteInsuranceFiles: [],
  isSubCustomer: false,
  parentCustomer: undefined,
  truckDetails: {
    vinNumber: '',
    plateNumber: '',
    unitNumber: '',
    color: '',
    make: '',
    model: '',
    year: '',
  },
};

// Default data for accounting customer
const defaultAccountingCustomerData: ICustomer = {
  company: '',
  email: '',
  autoScore: 100,
  stars: 5,
  phone: '',
  mobileNo: '',
  fax: '',
  other: '',
  website: '',
  notes: '',
  billingAddress: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  },
  shippingAddress: {
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  },
  sameAsBillingAddress: false,
  nameToPrintOnCheck: '',
  displayCustomerName: '',
  paymentMethod: PaymentMethods.NA,
  paymentTerms: '',
  isSubCustomer: false,
  parentCustomer: undefined,
  documents: [],
  deleteFiles: [],
  truckDetails: {
    vinNumber: '',
    plateNumber: '',
    unitNumber: '',
    color: '',
    make: '',
    model: '',
    year: '',
  },
};

export { freightCustomerSchema, accountingCustomerSchema };
export { defaultFreightCustomerData, defaultAccountingCustomerData };
